import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Trash2, RotateCcw, FileText, AlertTriangle, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper para formatear fechas SIN problemas de timezone
const formatDateSafe = (dateStr, options = { day: 'numeric', month: 'short', year: 'numeric' }) => {
  if (!dateStr) return 'N/A';
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString('es-MX', options);
};

export default function Papelera() {
  const [tratos, setTratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, trato: null });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, trato: null });
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFraccionamiento, setFilterFraccionamiento] = useState('todos');
  const [filterManzana, setFilterManzana] = useState('todas');

  useEffect(() => {
    fetchTratos();
  }, []);

  const fetchTratos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tratos/papelera`, { credentials: 'include' });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al cargar papelera');
      }
      
      const data = await response.json();
      setTratos(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Obtener fraccionamientos y manzanas únicos de los tratos
  const fraccionamientosUnicos = [...new Set(
    tratos.flatMap(t => t.lotes_info?.map(l => l.fraccionamiento_nombre) || []).filter(Boolean)
  )].sort();
  
  const manzanasUnicas = [...new Set(
    tratos.flatMap(t => t.lotes_info?.map(l => l.manzana) || []).filter(Boolean)
  )].sort();

  // Filtrar tratos
  const tratosFiltrados = tratos.filter(trato => {
    const lotes = trato.lotes_info || [];
    
    // Búsqueda por cliente o número de lote
    const matchSearch = searchTerm === '' || 
      trato.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lotes.some(l => l.numero?.toString().includes(searchTerm));
    
    // Filtro por fraccionamiento
    const matchFracc = filterFraccionamiento === 'todos' || 
      lotes.some(l => l.fraccionamiento_nombre === filterFraccionamiento);
    
    // Filtro por manzana
    const matchManzana = filterManzana === 'todas' || 
      lotes.some(l => l.manzana === filterManzana);
    
    return matchSearch && matchFracc && matchManzana;
  });

  const handleRestore = async () => {
    if (!restoreDialog.trato) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tratos/${restoreDialog.trato.trato_id}/restaurar`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al restaurar');
      }
      
      toast.success('Trato restaurado correctamente');
      setRestoreDialog({ open: false, trato: null });
      fetchTratos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteDialog.trato) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tratos/${deleteDialog.trato.trato_id}/permanente`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al eliminar');
      }
      
      toast.success('Trato eliminado permanentemente');
      setDeleteDialog({ open: false, trato: null });
      fetchTratos();
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="papelera-page" className="min-h-screen bg-zinc-950 p-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Papelera</h1>
          <p className="text-zinc-400 mt-2">Tratos eliminados - Puedes restaurar o eliminar permanentemente</p>
        </div>
        <div className="text-sm text-zinc-500">
          {tratosFiltrados.length} de {tratos.length} tratos
        </div>
      </div>

      {/* Barra de filtros */}
      {tratos.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Búsqueda */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="search-papelera"
                placeholder="Buscar por cliente o lote..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            {/* Filtro por fraccionamiento */}
            {fraccionamientosUnicos.length > 0 && (
              <div className="min-w-[180px]">
                <Select value={filterFraccionamiento} onValueChange={setFilterFraccionamiento}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <Filter className="w-4 h-4 mr-2 text-zinc-500" />
                    <SelectValue placeholder="Fraccionamiento" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="todos" className="text-white">Todos los fracc.</SelectItem>
                    {fraccionamientosUnicos.map(fracc => (
                      <SelectItem key={fracc} value={fracc} className="text-white">{fracc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Filtro por manzana */}
            {manzanasUnicas.length > 0 && (
              <div className="min-w-[150px]">
                <Select value={filterManzana} onValueChange={setFilterManzana}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Manzana" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="todas" className="text-white">Todas las manzanas</SelectItem>
                    {manzanasUnicas.map(mz => (
                      <SelectItem key={mz} value={mz} className="text-white">Manzana #{mz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {tratos.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <Trash2 className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">La papelera está vacía</h3>
          <p className="text-zinc-500">Los tratos eliminados aparecerán aquí</p>
        </div>
      ) : tratosFiltrados.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-16 text-center">
          <Search className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
          <h3 className="text-lg font-bold text-white mb-2">No hay resultados</h3>
          <p className="text-zinc-500">No se encontraron tratos con los filtros seleccionados</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tratosFiltrados.map((trato) => {
            const lotes = trato.lotes_info || [];
            let ubicacionLabel = '';
            
            if (lotes.length === 1) {
              const lote = lotes[0];
              const parts = [`Lote ${lote.numero}`];
              if (lote.manzana) parts.push(`Manzana #${lote.manzana}`);
              if (lote.fraccionamiento_nombre) parts.push(lote.fraccionamiento_nombre);
              ubicacionLabel = parts.join(' · ');
            } else if (lotes.length > 1) {
              const lotesNums = lotes.map(l => l.numero).join(' y ');
              const manzanas = [...new Set(lotes.map(l => l.manzana).filter(Boolean))];
              const fracc = lotes[0]?.fraccionamiento_nombre;
              
              let label = `Lote ${lotesNums}`;
              if (manzanas.length === 1) {
                label += ` Manzana #${manzanas[0]}`;
              } else if (manzanas.length > 1) {
                label += ` Manzana #${manzanas.join(', #')}`;
              }
              if (fracc) label += ` · ${fracc}`;
              ubicacionLabel = label;
            } else {
              ubicacionLabel = 'Sin lotes';
            }
            
            return (
              <div key={trato.trato_id} data-testid={`papelera-trato-${trato.trato_id}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="p-3 bg-red-500/10 rounded-lg">
                      <FileText className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-medium rounded">
                          {ubicacionLabel}
                        </span>
                        {lotes.length > 1 && (
                          <span className="px-2 py-0.5 bg-zinc-700 text-zinc-300 text-xs font-medium rounded">
                            {lotes.length} lotes
                          </span>
                        )}
                        <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs font-medium rounded">
                          Eliminado
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-white">
                        {trato.cliente_nombre || 'Cliente desconocido'}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">
                            Precio Total 
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${trato.moneda === 'MXN' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                              {trato.moneda || 'USD'}
                            </span>
                          </p>
                          <p className="font-mono font-medium text-white">
                            {trato.moneda === 'MXN' ? 'MXN ' : 'USD '}${trato.precio_final?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Enganche</p>
                          <p className="font-mono font-medium text-white">${trato.enganche?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Fecha Inicio</p>
                          <p className="font-mono font-medium text-white">{formatDateSafe(trato.fecha_inicio)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-zinc-500 mb-1">Eliminado</p>
                          <p className="font-mono font-medium text-red-400">{formatDateSafe(trato.deleted_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex gap-2">
                      <Button
                        data-testid={`restore-btn-${trato.trato_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setRestoreDialog({ open: true, trato: trato })}
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Restaurar
                      </Button>
                      <Button
                        data-testid={`delete-permanent-btn-${trato.trato_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, trato: trato })}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AlertDialog Restaurar */}
      <AlertDialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ ...restoreDialog, open })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Restaurar este trato?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              El trato volverá a aparecer en la lista principal de tratos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore} className="bg-emerald-500 hover:bg-emerald-600 text-white">Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog Eliminar Permanentemente */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <AlertDialogTitle className="text-white">¿Eliminar permanentemente?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-zinc-400">
              <strong className="text-red-400">Esta acción NO se puede deshacer.</strong>
              <br /><br />
              Se eliminarán permanentemente:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>El trato y toda su información</li>
                <li>Todos los pagos asociados</li>
                <li>Los lotes volverán a estar disponibles</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePermanent} className="bg-red-500 hover:bg-red-600 text-white">
              Eliminar Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
